/**
 * @file ConfirmSupplierCodeModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-24
 * @modified 2026-08-24
 * @description The last step of raising a new blend generation: the supplier
 *              has replied, so their item code becomes ours.
 *
 *              Until this point the item is local only — deliberately. We adopt
 *              the supplier's code, and item_code is the join key into invoice
 *              lines, purchase-order line items and the Epos mapping, so pushing
 *              a provisional code and renaming it later would churn that key
 *              while it is already in use. Confirming here writes the code once
 *              and creates the item in Xero for the first time.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import { useSlideOutButton } from './SlideOutButton';

function ConfirmSupplierCodeBody({ row, itemCode, setItemCode, itemName, setItemName, saving, error }) {
  // Called from the component that renders the buttons, so the measurement
  // happens in the same render cycle they mount in — see CLAUDE.md.
  useSlideOutButton([saving]);

  return (
    <div className="csc-body">
      <p className="csc-lede">
        This blend is not in Xero yet. Enter the code the supplier gave you — it
        becomes our item code, and the item is created in Xero when you confirm.
      </p>

      <div className="csc-current">
        <span className="csc-label">Currently</span>
        <span className="csc-value">{row.item_code} · {row.item_name}</span>
      </div>

      <div className="csc-field">
        <label htmlFor="csc-code">Supplier item code</label>
        <Input id="csc-code" type="text" value={itemCode} autoFocus
               onChange={(e) => setItemCode(e.target.value)}
               placeholder="e.g. 10086412" disabled={saving} />
      </div>

      <div className="csc-field">
        <label htmlFor="csc-name">Item name</label>
        <Input id="csc-name" type="text" value={itemName}
               onChange={(e) => setItemName(e.target.value)}
               placeholder="Name as it should appear" disabled={saving} />
      </div>

      {error && <p className="csc-error">{error}</p>}
    </div>
  );
}

function ConfirmSupplierCodeModal({ row, onClose, onConfirmed }) {
  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState(row?.item_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!itemCode.trim()) { setError('The supplier item code is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/formulations/${row.id}/confirm-supplier-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ item_code: itemCode.trim(), item_name: itemName.trim() })
      });
      const data = await res.json().catch(() => ({}));
      // Sync-or-fail on the server: a rejection means nothing was written, so
      // the modal stays open with what was typed and Confirm retries.
      if (!res.ok) throw new Error(data.error || 'Request failed');
      onConfirmed(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;

  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title="Confirm supplier code"
      className="confirm-supplier-code"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={submit} disabled={saving}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>
                  {saving ? 'progress_activity' : 'assignment_turned_in'}
                </span>
              </div>
              <span className="btn-label">{saving ? 'Creating in Xero…' : 'Confirm'}</span>
            </button>
          </div>
        </div>
      }
    >
      <ConfirmSupplierCodeBody
        row={row}
        itemCode={itemCode} setItemCode={setItemCode}
        itemName={itemName} setItemName={setItemName}
        saving={saving} error={error}
      />
    </Modal>
  );
}

export default ConfirmSupplierCodeModal;
